import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Close as CloseIcon } from '@material-ui/icons'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { formatPortalDate } from './portalShared'

const CONNECTION_TYPE_LABELS = {
  hose_bib: 'hose bib',
  hydrant: 'hydrant',
  overhead_dump: 'overhead dump',
  pond_lake: 'pond / lake',
  tank_cistern: 'tank / cistern',
  spigot: 'spigot',
  quick_coupler: 'quick coupler',
  other: 'other'
}

const formatConnectionTypes = (waterAccess = {}) => {
  const types = Array.isArray(waterAccess.connectionTypes) && waterAccess.connectionTypes.length
    ? waterAccess.connectionTypes
    : [waterAccess.connectionType].filter(Boolean)
  return [...new Set(types)]
    .map(type => CONNECTION_TYPE_LABELS[type] || String(type).replace(/_/g, ' '))
    .join(', ')
}

// ── Styles ───────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 560,
    background: 'var(--df-bg)'
  },
  scrollContainer: {
    overflowY: 'auto',
    flex: 1,
    padding: theme.spacing(2.5)
  },

  // Header (sticky)
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: 'var(--df-bg)',
    padding: '20px 24px 14px',
    borderBottom: '1px solid var(--df-line)'
  },
  headerTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  siteName: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--df-ink)',
    lineHeight: 1.3,
    flex: 1,
    marginRight: 12
  },
  siteAddress: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 11,
    color: 'var(--df-ink-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginTop: 4
  },
  chipRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 10
  },

  // Map
  mapContainer: {
    width: '100%',
    height: 200,
    borderRadius: 10,
    border: '1px solid var(--df-line)',
    overflow: 'hidden',
    background: 'var(--df-surface-2)'
  },
  mapPlaceholder: {
    width: '100%',
    height: 200,
    borderRadius: 10,
    border: '1px solid var(--df-line)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--df-surface-2)',
    color: 'var(--df-ink-4)',
    fontSize: 13
  },

  // Section card
  sectionCard: {
    background: 'var(--df-surface)',
    border: '1px solid var(--df-line)',
    borderRadius: 10,
    padding: '14px 18px'
  },
  overline: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    color: 'var(--df-ink-3)',
    marginBottom: 10
  },

  // Spray scope stats
  statRow: {
    display: 'flex',
    gap: 12
  },
  statCell: {
    flex: 1,
    textAlign: 'center'
  },
  statNumber: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 22,
    fontWeight: 600,
    color: 'var(--df-ink)'
  },
  statLabel: {
    fontSize: 11,
    color: 'var(--df-ink-3)',
    marginTop: 2
  },

  // Area list
  areaRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 0'
  },
  areaRule: {
    width: 3,
    alignSelf: 'stretch',
    borderRadius: 2,
    flexShrink: 0
  },
  areaBody: {
    flex: 1,
    minWidth: 0
  },
  areaName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--df-ink)'
  },
  areaSubtitle: {
    fontSize: 11,
    color: 'var(--df-ink-3)',
    marginTop: 2
  },
  areaModifiers: {
    fontSize: 10.5,
    color: 'var(--df-ink-4)',
    marginTop: 3,
    fontStyle: 'italic'
  },
  areaAcres: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 12,
    color: 'var(--df-ink-2)',
    flexShrink: 0,
    marginTop: 2
  },

  // Schedule 3-col
  scheduleRow: {
    display: 'flex',
    gap: 12
  },
  scheduleCell: {
    flex: 1
  },
  scheduleValue: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 13,
    color: 'var(--df-ink)',
    marginTop: 4
  },
  scheduleValueDim: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 13,
    color: 'var(--df-ink-4)',
    marginTop: 4
  },
  scheduleValueDanger: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 13,
    color: 'var(--df-danger-text, #a9341d)',
    fontWeight: 600,
    marginTop: 4
  },

  // Contact
  contactName: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  contactLink: {
    fontSize: 13,
    color: 'var(--df-primary)',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' }
  },
  dimText: {
    fontSize: 13,
    color: 'var(--df-ink-4)',
    fontStyle: 'italic'
  },
  accessLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--df-ink)'
  },
  accessValue: {
    fontSize: 13,
    color: 'var(--df-ink-2)'
  },

  // Loading / Error
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    gap: 16
  }
}))

// ── Helpers ──────────────────────────────────────────────────────

const SPRAYABLE_COLOR = '#2f7a3e'
const NON_SPRAYABLE_COLOR = '#97a094'

function getStatusChip (detail) {
  if (!detail) return null
  if (detail.status === 'overdue') {
    const days = detail.daysOverdue || detail.daysLate || '?'
    return { label: `Overdue ${days}d`, className: 'df-chip-danger' }
  }
  if (detail.status === 'due_soon') {
    const days = detail.daysTilDue || '?'
    return { label: `Due in ${days}d`, className: 'df-chip-warn' }
  }
  if (detail.status === 'never_done') {
    return { label: 'Never applied', className: 'df-chip-warn' }
  }
  if (detail.status === 'ok') {
    return { label: 'On schedule', className: 'df-chip-ok' }
  }
  return null
}

function getAssignmentChip (detail) {
  if (!detail) return null
  if (detail.assignedCrewName) {
    return { label: `Crew \u00B7 ${detail.assignedCrewName}`, className: 'df-chip-ok' }
  }
  return { label: 'Unassigned', className: 'df-chip-warn' }
}

function buildModifierText (area) {
  const parts = []
  if (area.weedPressure) parts.push(`Weed pressure: ${area.weedPressure}`)
  if (area.slope) parts.push(`Slope: ${area.slope}`)
  if (area.access) parts.push(`Access: ${area.access}`)
  return parts.length ? parts.join(' · ') : ''
}

function buildMarkerIcon () {
  return L.divIcon({
    className: '',
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#1f5e3a;border:2px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.25);"></div>',
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  })
}

// ── Component ────────────────────────────────────────────────────

const SubcontractorSiteDetailDrawer = ({ scheduleItemId, open, onClose }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState(null)
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)

  // ── Data fetch ──
  const fetchDetail = useCallback(async (id) => {
    setLoading(true)
    setError('')
    setDetail(null)
    try {
      const response = await axios.get(`/api/subcontractor/jobsites/${id}/detail`)
      setDetail(response.data?.item || null)
    } catch (err) {
      console.error('Failed to load site detail', err)
      setError(err?.response?.data?.message || 'Unable to load site detail.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (scheduleItemId) {
      fetchDetail(scheduleItemId)
    } else {
      setDetail(null)
      setLoading(false)
      setError('')
    }
  }, [scheduleItemId, fetchDetail])

  // ── Map init / update ──
  useEffect(() => {
    // Clean up old map when detail changes or drawer closes
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    if (!detail || !open) return

    const hasCoords = Number.isFinite(detail.latitude) && Number.isFinite(detail.longitude)
    if (!hasCoords) return

    // Small delay to let the DOM render the map container
    const timer = window.setTimeout(() => {
      const container = mapContainerRef.current
      if (!container) return

      const map = L.map(container, {
        center: [detail.latitude, detail.longitude],
        zoom: 16,
        zoomControl: true,
        attributionControl: false
      })

      L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
        maxZoom: 20,
        minZoom: 0
      }).addTo(map)

      mapInstanceRef.current = map

      // Draw area polygons
      const areas = detail.areas || []
      const polygonBounds = []

      areas.forEach((area) => {
        if (!area.paths || !area.paths.length) return
        const latlngs = area.paths.map(p => [p.lat, p.lng])
        const isSprayable = area.isSprayable !== false
        const fillColor = area.fillColor || (isSprayable ? SPRAYABLE_COLOR : NON_SPRAYABLE_COLOR)
        const strokeColor = area.strokeColor || fillColor

        const polygon = L.polygon(latlngs, {
          fillColor,
          fillOpacity: 0.3,
          color: strokeColor,
          weight: 2
        })
        polygon.addTo(map)
        polygonBounds.push(...latlngs)
      })

      // Add center marker
      L.marker([detail.latitude, detail.longitude], { icon: buildMarkerIcon() }).addTo(map)

      // Fit bounds or center on marker
      window.requestAnimationFrame(() => {
        if (!mapInstanceRef.current) return
        mapInstanceRef.current.invalidateSize()
        if (polygonBounds.length) {
          mapInstanceRef.current.fitBounds(polygonBounds, { padding: [20, 20] })
        } else {
          mapInstanceRef.current.setView([detail.latitude, detail.longitude], 16)
        }
      })
    }, 80)

    return () => {
      window.clearTimeout(timer)
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [detail, open])

  // ── Derived ──
  const statusChip = getStatusChip(detail)
  const assignmentChip = getAssignmentChip(detail)
  const hasCoords = detail && Number.isFinite(detail.latitude) && Number.isFinite(detail.longitude)
  const areaSummary = detail?.areaSummary || {}
  const areas = detail?.areas || []

  // ── Render ──
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      {/* ── 1. Header (sticky) ── */}
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.siteName}>
              {detail?.siteName || detail?.propertyName || 'Site Detail'}
            </Typography>
            <Typography className={classes.siteAddress}>
              {detail?.address || 'No address'}
            </Typography>
          </div>
          <IconButton size="small" onClick={onClose} style={{ marginTop: -4 }}>
            <CloseIcon />
          </IconButton>
        </div>
        {detail && (
          <div className={classes.chipRow}>
            {statusChip && <Chip size="small" label={statusChip.label} className={statusChip.className} />}
            {assignmentChip && <Chip size="small" label={assignmentChip.label} className={assignmentChip.className} />}
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      {loading ? (
        <div className={classes.centerBlock}>
          <CircularProgress size={28} />
          <Typography variant="body2" style={{ color: 'var(--df-ink-3)' }}>Loading site detail…</Typography>
        </div>
      ) : error ? (
        <div className={classes.centerBlock}>
          <Typography variant="body2" style={{ color: 'var(--df-danger-text, #a9341d)' }}>{error}</Typography>
        </div>
      ) : detail ? (
        <div className={classes.scrollContainer}>
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>

            {/* ── 2. Map ── */}
            {hasCoords ? (
              <div ref={mapContainerRef} className={classes.mapContainer} />
            ) : (
              <div className={classes.mapPlaceholder}>No coordinates available</div>
            )}

            {/* ── 3. Spray Scope ── */}
            <div className={classes.sectionCard}>
              <div className={classes.overline}>Spray Scope</div>
              <div className={classes.statRow}>
                <div className={classes.statCell}>
                  <div className={classes.statNumber}>{areaSummary.totalAcres ?? '—'}</div>
                  <div className={classes.statLabel}>Total acres</div>
                </div>
                <div className={classes.statCell}>
                  <div className={classes.statNumber}>{areaSummary.sprayableAcres ?? '—'}</div>
                  <div className={classes.statLabel}>Sprayable acres</div>
                </div>
                <div className={classes.statCell}>
                  <div className={classes.statNumber}>{areaSummary.totalCount ?? '—'}</div>
                  <div className={classes.statLabel}>Total areas</div>
                </div>
                <div className={classes.statCell}>
                  <div className={classes.statNumber}>{areaSummary.sprayableCount ?? '—'}</div>
                  <div className={classes.statLabel}>Sprayable</div>
                </div>
              </div>
            </div>

            {/* ── 4. Area List ── */}
            {areas.length > 0 && (() => {
              // Build coverage type breakdown from coverageType label
              const typeBreakdown = {}
              areas.forEach(a => {
                const ct = a.coverageType || 'Other'
                if (!typeBreakdown[ct]) typeBreakdown[ct] = { count: 0, acres: 0 }
                typeBreakdown[ct].count++
                typeBreakdown[ct].acres += (a.areaAcres || 0)
              })
              const typeEntries = Object.entries(typeBreakdown).sort((a, b) => b[1].acres - a[1].acres)

              return (
                <div>
                  <div className={classes.overline}>Spray Areas</div>

                  {/* Coverage type breakdown */}
                  {typeEntries.length > 0 && (
                    <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginBottom: 10 }}>
                      {typeEntries.map(([type, info]) => (
                        <Chip
                          key={type}
                          size="small"
                          label={`${type}: ${info.count} · ${info.acres ? info.acres.toFixed(2) + ' ac' : ''}`}
                          className="df-chip-ok"
                        />
                      ))}
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
                    {areas.map((area, idx) => (
                      <div key={area._id || idx} className={classes.areaRow}>
                        <div className={classes.areaRule} style={{ background: SPRAYABLE_COLOR }} />
                        <div className={classes.areaBody}>
                          <div className={classes.areaName}>{area.name || 'Unnamed area'}</div>
                          <div className={classes.areaSubtitle}>{area.coverageType || 'Spray area'}</div>
                        </div>
                        <div className={classes.areaAcres}>
                          {area.areaAcres ? `${area.areaAcres} ac` : area.areaSF ? `${area.areaSF.toLocaleString()} sf` : '—'}
                        </div>
                      </div>
                    ))}
                  </Box>
                </div>
              )
            })()}

            {/* ── 5. Schedule & Status ── */}
            <div className={classes.sectionCard}>
              <div className={classes.scheduleRow}>
                <div className={classes.scheduleCell}>
                  <div className={classes.overline} style={{ marginBottom: 0 }}>Last Applied</div>
                  <div className={detail.lastAppliedDate ? classes.scheduleValue : classes.scheduleValueDim}>
                    {detail.lastAppliedDate
                      ? formatPortalDate(detail.lastAppliedDate)
                      : 'No history'}
                  </div>
                </div>
                <div className={classes.scheduleCell}>
                  <div className={classes.overline} style={{ marginBottom: 0 }}>Next Due</div>
                  <div className={detail.status === 'overdue' ? classes.scheduleValueDanger : (detail.nextScheduledDate ? classes.scheduleValue : classes.scheduleValueDim)}>
                    {detail.nextScheduledDate
                      ? formatPortalDate(detail.nextScheduledDate)
                      : 'Not set'}
                  </div>
                </div>
                <div className={classes.scheduleCell}>
                  <div className={classes.overline} style={{ marginBottom: 0 }}>Blue Dye</div>
                  <div className={detail.blueDye ? classes.scheduleValue : classes.scheduleValueDim}>
                    {detail.blueDye || 'Unset'}
                  </div>
                </div>
              </div>

              {/* Queue / Priority / Vendor Notes */}
              <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 12 }}>
                {detail.queueStatus && (
                  <Chip size="small" label={detail.queueStatus} className="df-chip-info" />
                )}
                {detail.priority && detail.priority !== 'normal' && (
                  <Chip size="small" label={`Priority: ${detail.priority}`} className="df-chip-warn" />
                )}
              </Box>
              {detail.vendorNotes && (
                <Typography variant="body2" style={{ color: 'var(--df-ink-2)', marginTop: 10, fontSize: 13 }}>
                  {detail.vendorNotes}
                </Typography>
              )}
            </div>

            {/* ── 6. Access ── */}
            <div className={classes.sectionCard}>
              <div className={classes.overline}>Access</div>
              <div>
                <span className={classes.accessLabel}>Gate code: </span>
                {detail.gateCode
                  ? <span className={classes.accessValue} style={{ fontWeight: 600 }}>{detail.gateCode}</span>
                  : <span className={classes.dimText}>No gate code</span>}
              </div>
              {detail.siteNotes && (
                <Typography variant="body2" style={{ color: 'var(--df-ink-2)', marginTop: 8, fontSize: 13 }}>
                  {detail.siteNotes}
                </Typography>
              )}
              {detail.waterAccess && (
                <Box mt={1}>
                  <div className={classes.overline} style={{ marginTop: 8 }}>Water Access</div>
                  {(detail.waterAccess.connectionType || detail.waterAccess.connectionTypes?.length) && (
                    <div style={{ fontSize: 13, color: 'var(--df-ink-2)' }}>
                      Connection: {formatConnectionTypes(detail.waterAccess)} {detail.waterAccess.connectionSize ? ` (${detail.waterAccess.connectionSize})` : ''}
                    </div>
                  )}
                  {detail.waterAccess.siteHasAdapter && (
                    <div style={{ fontSize: 13, color: 'var(--df-ink-2)', marginTop: 4 }}>
                      Adapter/tool: available on site
                    </div>
                  )}
                  {detail.waterAccess.locationNotes && (
                    <div style={{ fontSize: 13, color: 'var(--df-ink-2)', marginTop: 4 }}>
                      Location: {detail.waterAccess.locationNotes}
                    </div>
                  )}
                  {detail.waterAccess.estimatedFillMinutes && (
                    <div style={{ fontSize: 13, color: 'var(--df-ink-2)', marginTop: 4 }}>
                      Fill time: {detail.waterAccess.estimatedFillMinutes} min
                    </div>
                  )}
                </Box>
              )}
            </div>

            {/* ── 7. Contact ── */}
            <div className={classes.sectionCard}>
              <div className={classes.overline}>Contact</div>
              {detail.primaryContact ? (
                <div>
                  <div className={classes.contactName}>{detail.primaryContact.name}</div>
                  {detail.primaryContact.phone && (
                    <div style={{ marginTop: 4 }}>
                      <a href={`tel:${detail.primaryContact.phone}`} className={classes.contactLink}>
                        {detail.primaryContact.phone}
                      </a>
                    </div>
                  )}
                  {detail.primaryContact.email && (
                    <div style={{ marginTop: 4 }}>
                      <a href={`mailto:${detail.primaryContact.email}`} className={classes.contactLink}>
                        {detail.primaryContact.email}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className={classes.dimText}>No contact on file</div>
              )}
            </div>

          </Box>
        </div>
      ) : null}
    </Drawer>
  )
}

export default SubcontractorSiteDetailDrawer
